import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { routes } from "@app/routing";
import { useIntakeForm } from "./context";

/**
 * Guard for the editable parts of the GIS flow (intake steps and patient
 * education). Once the patient has confirmed their appointment the intake
 * answers are locked, so this hook returns a redirect element that bounces
 * the patient out of any editable step back to a terminal destination —
 * preventing them from navigating "back" to edit a confirmed booking.
 *
 * Post-test patients are sent to their test result details page (the same
 * destination as the confirmation screen's "Go to My Results" action); every
 * other case (pre-test, no-order) is sent to the portal home.
 *
 * Returns `null` while no booking is confirmed so callers can render normally.
 */
export function useBookingConfirmedRedirect(): React.ReactElement | null {
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const { bookingConfirmed, counselingIntent } = useIntakeForm();

  if (!bookingConfirmed) {
    return null;
  }

  const target =
    counselingIntent === "post-test" && orderUidPublic && testUid
      ? routes.testResultDetailsPage(orderUidPublic, testUid)
      : routes.home;

  return <Navigate to={target} replace />;
}
